import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import {
    ArrowTopRightOnSquareIcon,
    ArrowRightIcon,
    BoltIcon,
    ServerStackIcon,
    LinkIcon,
    PresentationChartLineIcon,
} from '@heroicons/react/24/outline'
import { Footer } from '@/components/Footer'
import { Navbar } from '@/components/Navbar'
import { Spotlight } from '@/components/ui/spotlight'
import { Typewriter } from '@/components/ui/typewriter'

const ease: [number, number, number, number] = [0.16, 1, 0.3, 1]
const stagger = 0.04

function MouseGlow() {
    const ref = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            if (ref.current) {
                ref.current.style.background = `radial-gradient(800px circle at ${e.clientX}px ${e.clientY}px, rgba(110,31,27,0.07), transparent 50%)`
            }
        }
        window.addEventListener('mousemove', onMove)
        return () => window.removeEventListener('mousemove', onMove)
    }, [])
    return <div ref={ref} className="pointer-events-none fixed inset-0 z-30 hidden lg:block" />
}

const platforms = [
    {
        name: 'TurboRAG',
        num: '01',
        tag: 'Frontier Retrieval',
        lead: 'Exact context at sub-100ms.',
        body: 'Production-grade retrieval for systems where recall and latency both matter. Designed for developers, not demos.',
        href: 'https://turborag.jpdz.app/',
        icon: BoltIcon,
        chips: ['Sub-100ms', 'Exact windows', 'Production recall'],
    },
    {
        name: 'A/MCL',
        num: '02',
        tag: 'Agent Memory',
        lead: 'Persistent memory across agents.',
        body: 'Contextual handoffs across Cursor, Claude Code, and more. Zero-config continuity — stop explaining everything twice.',
        href: 'https://amcl.jpdz.app/',
        icon: ServerStackIcon,
        chips: ['Cross-agent', 'Decision log', 'File-backed'],
    },
    {
        name: 'Skill Composer',
        num: '03',
        tag: 'Skill Pipelines',
        lead: 'Chain AI skills into pipelines automatically.',
        body: 'One install across 12+ agents — Claude Code, Cursor, Codex, and more. Reads your prompt, picks the right chain, runs the full pipeline. Zero runtime cost.',
        href: 'https://github.com/ratnam1510/skill-composer',
        icon: LinkIcon,
        chips: ['12+ agents', 'Auto-chaining', 'Zero latency'],
    },
    {
        name: 'Alphagen',
        num: '04',
        tag: 'Financial Intelligence',
        lead: 'Quantitative predictions with market context.',
        body: 'Advanced financial intelligence with NLP-based earnings analysis and ML-powered stock predictions.',
        href: 'https://alphagen.jpdz.app/',
        icon: PresentationChartLineIcon,
        chips: ['Earnings NLP', 'Stock prediction', 'Quant signals'],
    },
]


export function DeveloperPage() {
    const ref = useRef<HTMLDivElement>(null)
    const reduced = useReducedMotion()
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] })
    const heroOp = useTransform(scrollYProgress, [0, 0.07], [1, 0])
    const heroScale = useTransform(scrollYProgress, [0, 0.07], [1, 0.97])
    const m = (d: number) => reduced ? undefined : { duration: 0.7, delay: d, ease }

    return (
        <div ref={ref} className="min-h-screen bg-[#211d18] text-[#eee8dc]">
            <MouseGlow />

            <Navbar variant="dark" />

            {/* ── Hero — text only, full width ── */}
            <motion.section style={reduced ? undefined : { opacity: heroOp, scale: heroScale }} className="relative flex min-h-[96svh] items-center overflow-hidden pt-28 sm:pt-36">
                <Spotlight className="-top-40 left-0 md:left-60 md:-top-20" fill="#6e1f1b" />

                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(90deg, rgba(238,232,220,0.035) 1px, transparent 1px), linear-gradient(rgba(238,232,220,0.025) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
                <div className="absolute bottom-0 left-0 right-0 h-52 bg-gradient-to-t from-[#211d18] to-transparent" />

                <div className="relative z-10 mx-auto max-w-[1400px] px-4 sm:px-8">
                    <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={m(0.08)} className="mb-6 font-body text-[11px] uppercase tracking-[0.3em] text-[#c8a88a]/45">
                        Developer surface
                    </motion.p>

                    <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={m(0.14)} className="max-w-[1100px] font-serif text-[clamp(3.4rem,9vw,9.5rem)] font-medium leading-[0.84]">
                        <span className="text-[#eee8dc]">Tools that</span>
                        <br />
                        <span className="inline-block font-serif italic text-[#c8a88a]" style={{ minWidth: '11ch' }}>
                            <Typewriter
                                text={["ship.", "retrieve.", "remember.", "accelerate.", "build."]}
                                speed={70}
                                deleteSpeed={40}
                                waitTime={2000}
                                className="text-[#c8a88a]"
                                cursorChar="_"
                                cursorClassName="ml-1 text-[#6e1f1b]"
                            />
                        </span>
                    </motion.h1>

                    <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={m(0.28)} className="mt-10 max-w-xl font-body text-[17px] leading-[1.85] text-[#eee8dc]/40">
                        Install once, remove friction forever. Retrieval, memory, IDE tooling, CLI dashboards — built for developers who care about quality.
                    </motion.p>

                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={m(0.36)} className="mt-10 flex flex-wrap gap-4">
                        <a href="#platforms" className="group inline-flex min-h-[48px] items-center gap-3 bg-[#6e1f1b] px-7 font-body text-[13px] font-semibold text-[#eee8dc] transition-all duration-300 hover:bg-[#8b2e28] hover:shadow-[0_12px_40px_rgba(110,31,27,0.25)]">
                            Explore platforms
                            <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
                        </a>
                    </motion.div>
                </div>
            </motion.section>

            {/* ── Platforms — alternating editorial spreads ── */}
            <div id="platforms">
            {platforms.map((p, i) => (
                <motion.section key={p.name} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: '-120px' }} transition={{ duration: 0.9, ease }} className="relative border-t border-[#eee8dc]/[0.04]">
                    <a href={p.href} target="_blank" rel="noopener noreferrer" className="group block">
                        <div className={`relative mx-auto grid max-w-[1400px] items-center gap-10 px-4 py-24 sm:px-8 sm:py-32 lg:min-h-[60vh] lg:gap-20 lg:grid-cols-2`}>
                            <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100" style={{ background: `radial-gradient(ellipse at ${i % 2 === 0 ? '30%' : '70%'} 50%, rgba(110,31,27,0.04) 0%, transparent 60%)` }} />

                            <motion.div initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.7, delay: 0.1, ease }} className={i % 2 === 1 ? 'lg:order-2' : ''}>
                                <span className="mb-4 block font-serif text-[clamp(6rem,12vw,11rem)] font-medium leading-none text-[#eee8dc]/[0.03]">{p.num}</span>
                                <div className="flex items-center gap-3">
                                    <span className="font-body text-[9px] uppercase tracking-[0.28em] text-[#6e1f1b]">{p.tag}</span>
                                    <ArrowTopRightOnSquareIcon className="h-3 w-3 text-[#eee8dc]/[0.12] transition-all duration-300 group-hover:text-[#c8a88a] group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </div>
                                <h2 className="mt-2 font-serif text-[clamp(3.2rem,7vw,7rem)] font-medium leading-[0.86] text-[#eee8dc] transition-colors duration-500 group-hover:text-[#c8a88a]">
                                    {p.name}
                                </h2>
                            </motion.div>

                            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.6, delay: 0.2, ease }} className={i % 2 === 1 ? 'lg:order-1' : ''}>
                                <p className="mb-2 font-serif text-xl italic leading-relaxed text-[#eee8dc]/50">{p.lead}</p>
                                <p className="mb-8 max-w-lg font-body text-[15px] leading-[1.85] text-[#eee8dc]/35">{p.body}</p>
                                <div className="flex flex-wrap gap-2">
                                    {p.chips.map(c => (
                                        <span key={c} className="border border-[#eee8dc]/[0.05] px-3.5 py-1.5 font-body text-[10px] uppercase tracking-[0.14em] text-[#eee8dc]/25 transition-all duration-300 group-hover:border-[#6e1f1b]/25 group-hover:text-[#eee8dc]/50">{c}</span>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </a>
                </motion.section>
            ))}
            </div>


            {/* ── About ── */}
            <section className="relative overflow-hidden border-t border-[#eee8dc]/[0.04] bg-[#211d18] py-28 sm:py-36">
                <div className="pointer-events-none absolute right-[-4%] top-1/2 hidden -translate-y-1/2 select-none font-serif text-[20vw] leading-none text-[#eee8dc]/[0.015] lg:block" aria-hidden="true">J</div>
                <div className="pointer-events-none absolute left-[5%] top-[20%] hidden h-[400px] w-[400px] rounded-full opacity-50 lg:block" style={{ background: 'radial-gradient(circle, rgba(200,168,138,0.04) 0%, transparent 60%)' }} />

                <div className="relative mx-auto max-w-[1400px] px-4 sm:px-8">
                    <div className="grid gap-16 lg:grid-cols-[1fr_1.2fr]">
                        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.7, ease }}>
                            <p className="mb-4 font-body text-[11px] uppercase tracking-[0.24em] text-[#6e1f1b]">About the lab</p>
                            <h2 className="font-serif text-[clamp(2.8rem,5.5vw,5.5rem)] font-medium leading-[0.88] text-[#eee8dc]">
                                Taste and<br /><span className="italic text-[#c8a88a]">force.</span>
                            </h2>
                        </motion.div>

                        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.7, delay: 0.06, ease }} className="lg:pt-4">
                            <p className="mb-5 font-body text-[17px] leading-[1.85] text-[#eee8dc]/45">
                                Jpdz Labs builds AI-powered tools for developers who care about quality. Products use advanced models for genuine value — not affiliate noise, not dashboard theater.
                            </p>
                            <p className="mb-12 font-body text-[17px] leading-[1.85] text-[#eee8dc]/45">
                                Transparency, honesty, putting users first.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <Link to="/about" className="inline-flex min-h-[48px] items-center gap-2 border border-[#eee8dc]/[0.08] px-6 font-body text-[13px] text-[#eee8dc]/45 transition-all duration-300 hover:border-[#eee8dc]/[0.2] hover:text-[#eee8dc]">
                                    Learn more
                                </Link>
                                <Link to="/chat" className="group inline-flex min-h-[48px] items-center gap-3 bg-[#6e1f1b] px-7 font-body text-[13px] font-semibold text-[#eee8dc] transition-all duration-300 hover:bg-[#8b2e28] hover:shadow-[0_12px_40px_rgba(110,31,27,0.25)]">
                                    Try Jpdz
                                    <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="relative overflow-hidden bg-[#6e1f1b] py-24 sm:py-32">
                <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(55deg, transparent, transparent 40px, rgba(238,232,220,0.14) 40px, rgba(238,232,220,0.14) 41px)' }} />
                <div className="absolute left-0 right-0 top-0 h-16 bg-gradient-to-b from-[#211d18]/25 to-transparent" />
                <div className="pointer-events-none absolute right-[-2%] top-1/2 hidden -translate-y-1/2 select-none font-serif text-[16vw] leading-none text-[#f7f1e7]/[0.04] lg:block" aria-hidden="true">D</div>

                <div className="relative mx-auto max-w-[1400px] px-4 text-center sm:px-8">
                    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease }}>
                        <p className="mb-4 font-body text-[11px] uppercase tracking-[0.3em] text-[#eee8dc]/25">Get in touch</p>
                        <h2 className="mb-6 font-serif text-[clamp(2.4rem,5vw,5rem)] font-medium leading-[0.92] text-[#eee8dc]">Want to collaborate?</h2>
                        <a href="mailto:ratnam@jpdz.app" className="group inline-flex min-h-[48px] items-center gap-3 font-body text-[17px] text-[#eee8dc]/55 transition-colors duration-300 hover:text-[#eee8dc]">
                            <span className="relative">ratnam@jpdz.app<span className="absolute -bottom-1 left-0 h-px w-0 bg-[#eee8dc]/30 transition-all duration-500 group-hover:w-full" /></span>
                            <ArrowRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </a>
                    </motion.div>
                </div>
            </section>

            <Footer variant="dark" />
        </div>
    )
}

export default DeveloperPage
